import React, { useEffect, useState } from 'react';

// Utils
import { SortDirection } from 'ka-table/enums';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../store/AppContext';
import withAuth from '../../utils/hocs/withAuth';
import withSidebar from '../../utils/hocs/withSidebar';
import SCOPE from '../../utils/gate/scope';
import withPermission from '../../utils/hocs/withPermission';
import useNotification from '../../utils/hooks/useNotification';
import PermissionsGate, { isGranted } from '../../utils/gate/permissionsGate';

// Icons
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';

// Components
import PageWrapper from '../../components/layout/PageWrapper/PageWrapper';
import GenericTable from '../../components/tables/GenericTable/GenericTable';
import Button from '../../components/atoms/Button/Button';
import SkeletonLoader from '../../components/molecules/SkeletonLoader/SkeletonLoader';
import EmptyList from '../../components/organisms/EmptyList/EmptyList';

// Services
import usePackageService from '../../services/packageService';
import useCampService from '../../services/campService';

function Packages() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const location = useLocation();
  const navigate = useNavigate();

  const [{ user }] = useAppContext();

  const { displayNotification } = useNotification();

  const { getAllPackages } = usePackageService();
  const { getAllCamps } = useCampService();

  const [packages, setPackages] = useState([]);
  const [camps, setCamps] = useState([]);

  useEffect(() => {
    if (location?.state) {
      const notif = location.state;
      displayNotification({ message: t(notif) });
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const { isFetching: isFetchingPackages } = useQuery(
    ['getAllPackages'],
    () => getAllPackages(),
    { onSuccess: (res) => setPackages(res?.data?.packages || []) },
  );

  const { isFetching: isFetchingCamps } = useQuery(
    ['getAllCamps'],
    () => getAllCamps(),
    { onSuccess: (res) => setCamps(res?.data?.camps || []) },
  );

  const columns = {
    name: {
      key: 'name', title: t('packages.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    title: { key: 'title', title: t('packages.table.columns.title') },
    actions: { key: 'actions', title: t('packages.table.columns.actions') },
  };

  const getPackageRow = (pack, index) => ({
    id: index,
    name: pack.name,
    title: camps?.find((camp) => camp.id === pack.campId)?.name,
    actions: (
      <div>
        <a href={`/packages/edit?id=${pack.id}`} className="d-flex a-center c-primary-500 fw-500">
          <EditIcon className="m-r-10" />
          {t('packages.table.actions.edit.label')}
        </a>
      </div>
    ),
  });

  return (
    <PageWrapper
      title={t('packages.title')}
      hasWhiteWrapper
    >
      {isFetchingPackages || isFetchingCamps ? (
        <>
          <SkeletonLoader height={60} className="m-b-20" />
          <SkeletonLoader height={300} />
        </>
      ) : (
        <>
          {packages?.length ? (
            <GenericTable
              columns={[columns.name, columns.title, columns.actions]}
              rows={packages.map((pack, index) => getPackageRow(pack, index))}
              isSearchable
              searchPlaceholder={t('packages.table.search')}
            />
          ) : (
            <EmptyList
              imageTheme="products"
              title={t('packages.table.empty.title')}
              text={`
                ${t('packages.table.empty.text')} 
                ${isGranted(user, [SCOPE.create_ownEst_package]) ? t('packages.table.empty.add') : ''}
              `}
            />
          )}

          {!isFetchingPackages && !isFetchingCamps ? (
            <PermissionsGate scopes={[SCOPE.create_ownEst_package]}>
              <div className="d-flex a-center j-end sticky-actions mobile-a-stretch">
                <a href="/packages/new">
                  <Button full={isMobile}>
                    <PlusIcon className="m-r-10" />
                    {t('packages.add')}
                  </Button>
                </a>
              </div>
            </PermissionsGate>
          ) : null}
        </>
      )}
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(Packages),
    {
      scopes: [SCOPE.get_ownEst_package],
    },
  ),
);
